import { useAsync } from 'react-async-hook'
import { CacheFactory, type Cache } from '../api/v1/client/ui/services/cache'
import { useApiClient } from './api-client'
import type { ApiClientV1 } from '../api'

export interface IConfigurationVariable {
  key: string
  value: string
}

export interface IFetchConfigurationVariableOptions {
  suppressErrors?: boolean
  bypassCache?: boolean
}

export interface IUseConfigurationVariableOptions {
  bypassCache?: boolean
}

// Cache key type for configuration variables
interface ConfigurationVariableCacheKey {
  key: string
}

// Module-level cache instance (lazy-initialized to allow test control via localStorage)
let cacheInstance: Cache<
  ConfigurationVariableCacheKey,
  IConfigurationVariable | null
> | null = null

/**
 * Get the configuration variable cache instance (lazy initialization).
 * This allows tests to control cache configuration via localStorage before initialization.
 */
const getConfigurationVariableCache = (): Cache<
  ConfigurationVariableCacheKey,
  IConfigurationVariable | null
> => {
  if (!cacheInstance) {
    cacheInstance = CacheFactory.create<
      ConfigurationVariableCacheKey,
      IConfigurationVariable | null
    >(
      'configuration-variable',
      true, // enablePersistence
      true, // enableSWR
    )
  }
  return cacheInstance
}

/**
 * Reset the cache instance (primarily for testing).
 */
export const resetConfigurationVariableCacheInstance = () => {
  cacheInstance = null
}

/**
 * Fetches a configuration variable from the platform API.
 * This function can be used outside of React components.
 *
 * Results are cached using the SWR (Stale-While-Revalidate) strategy by default.
 * Pass `bypassCache: true` to skip the cache and always fetch fresh data.
 *
 * @param apiClient - The API client instance
 * @param key - The configuration key to fetch
 * @param options - Options for error handling and cache control
 * @param options.suppressErrors - When true, errors are caught and null is returned instead of rethrowing. Defaults to false.
 * @param options.bypassCache - When true, skips the cache and fetches fresh data. Defaults to false.
 * @returns The configuration variable or null if suppressErrors is true and an error occurs
 * @throws The caught error when suppressErrors is false (default)
 */
export const fetchConfigurationVariable = async (
  apiClient: ApiClientV1,
  key: string,
  options: IFetchConfigurationVariableOptions = {},
): Promise<IConfigurationVariable | null> => {
  const { suppressErrors = false, bypassCache = false } = options

  const fetch = async () => {
    try {
      const response =
        await apiClient.services.apiServer.get<IConfigurationVariable>(
          `/configuration/extension/${key}`,
          {},
          { rawResponse: true },
        )
      return response
    } catch (error) {
      if (suppressErrors) {
        console.error(`Error fetching configuration for key ${key}:`, error)
        return null
      }
      throw error
    }
  }

  if (bypassCache) {
    return fetch()
  }

  const cache = getConfigurationVariableCache()
  return cache.execute({ key }, fetch)
}

/**
 * Hook to fetch configuration variables from the platform API.
 * This hook fetches the configuration variable reactively when the component mounts
 * or when the key changes.
 *
 * Results are cached using the SWR (Stale-While-Revalidate) strategy — the cached
 * value is returned immediately while a background refresh is triggered. Pass
 * `bypassCache: true` to always fetch fresh data.
 *
 * Errors are suppressed internally — if the API call fails, `value` is `null`
 * and `loading` returns to `false`. Use `fetchConfigurationVariable` directly
 * if you need explicit error handling.
 *
 * @param key - The configuration key to fetch
 * @param options - Options for cache control
 * @param options.bypassCache - When true, skips the cache and fetches fresh data. Defaults to false.
 * @returns An object with the value and loading state
 *
 * @example
 * ```tsx
 * const { value, loading } = useConfigurationVariable('MY_CONFIG_KEY')
 *
 * if (loading) return <div>Loading...</div>
 * return <div>Config Value: {value ?? 'Not configured'}</div>
 * ```
 *
 * @example
 * ```tsx
 * // Bypass the cache to always fetch fresh data
 * const { value, loading } = useConfigurationVariable('MY_CONFIG_KEY', { bypassCache: true })
 * ```
 */
export const useConfigurationVariable = (
  key: string,
  options: IUseConfigurationVariableOptions = {},
) => {
  const { bypassCache = false } = options
  const apiClient = useApiClient()
  const { result, loading } = useAsync(
    (client: ApiClientV1, k: string, bypass: boolean) =>
      fetchConfigurationVariable(client, k, {
        suppressErrors: true,
        bypassCache: bypass,
      }),
    [apiClient, key, bypassCache],
  )

  return { value: result?.value ?? null, loading }
}
